import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DateRange } from "react-day-picker";
import { formatISO, isValid } from "date-fns";
import { fromJS } from "immutable";
import { components, MultiValueProps, MultiValueRemoveProps } from "react-select";
import { ChevronDown, X } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { DateRangePickerField, SelectField } from "../../../components/ui";
import { renderFieldIcon } from "../../../fields/FieldList";
import InputDebounce from "../../../components/InputDebounce";
import { FilterRuleProps } from "./AdvancedFilter";
import { search } from "../../../api/ProjectApi";
import { colorCodes } from "../../../board/components/TaskDetail/ColorPicker";

type AdvancedFilterItemProps = {
  availableFields: {
    label: string;
    value: string;
    data: any;
  }[];
  idx: number;
  filterRule: FilterRuleProps;
  onChange: (filterRule: FilterRuleProps) => void;
  onRemove: (idx: number) => void;
};

const textConditions = [
  { value: "regex", label: "contains" },
  { value: "ne_regex", label: "does not contain" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
  { value: "is", label: "is" },
  { value: "is_not", label: "is not" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];

const numberConditions = [
  { value: "is", label: "is" },
  { value: "is_not", label: "is not" },
  { value: "lt", label: "less than" },
  { value: "gt", label: "greater than" },
  { value: "lte", label: "less than or equal" },
  { value: "gte", label: "greater than or equal" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];

const selectConditions = [
  { value: "in", label: "has" },
  { value: "nin", label: "has not" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];

const dateTimeConditions = [
  { value: "dateRange", label: "between" },
  { value: "before", label: "before" },
  { value: "after", label: "after" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];

/** Operators that don't require a value input */
const noValueOperators = new Set(["empty", "not_empty"]);

const AdvancedFilterItem = ({
  availableFields,
  idx,
  filterRule,
  onChange,
  onRemove,
}: AdvancedFilterItemProps) => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();

  const [accountOptions, setAccountOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);

  const ruleReducer = useSelector((state: any) => state.ruleReducer);
  const valueByFields = ruleReducer.get(filterRule.fieldId)?.toJS() || [];

  useEffect(() => {
    if (filterRule.fieldId === "account") {
      search({
        projectId,
        pageSize: 50,
        types: ["COMPANY"],
        filters: {},
      }).then((result) => {
        const datas = fromJS(result)
          .getIn(["pageResult", "content"])
          .map((item) => ({
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]) || item.getIn(["data", "email", "value"]),
            data: item,
          }))
          .toJS();
        setAccountOptions(datas);
      });
    } else if (filterRule.fieldId === "contact") {
      search({
        projectId,
        pageSize: 50,
        types: ["CONTACT"],
        filters: {},
      }).then((result) => {
        const datas = fromJS(result)
          .getIn(["pageResult", "content"])
          .map((item) => ({
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]) || item.getIn(["data", "email", "value"]),
            data: item,
          }))
          .toJS();
        setContactOptions(datas);
      });
    }
  }, [filterRule.fieldId, projectId]);

  const isDateTime = (fieldId) => {
    const fieldData = availableFields.find((item) => item.value === fieldId);
    if (fieldData && fieldData.data && fieldData.data.type === "datetime") return true;
    return false;
  };

  const isSelectBox = (fieldId) => {
    const valueByFieldsData = ruleReducer.get(fieldId)?.toJS() || [];
    return valueByFieldsData.length > 0;
  };

  const isNumber = (fieldId) => {
    const fieldData = availableFields.find((item) => item.value === fieldId);
    if (
      fieldData &&
      fieldData.data &&
      (fieldData.data.type === "double" || fieldData.data.type === "percent")
    )
      return true;
    return false;
  };

  const getConditionOptions = () => {
    if (isDateTime(filterRule.fieldId)) return dateTimeConditions;
    if (isSelectBox(filterRule.fieldId)) return selectConditions;
    if (isNumber(filterRule.fieldId)) return numberConditions;
    if (filterRule.fieldId === "account" || filterRule.fieldId === "contact")
      return selectConditions;
    return textConditions;
  };

  const renderFieldValue = () => {
    if (filterRule.operator === "dateRange") {
      let dateTime: DateRange = undefined;
      if (filterRule.fieldValue && filterRule.fieldValue.length === 2) {
        const from = isValid(new Date(filterRule.fieldValue[0]))
          ? new Date(filterRule.fieldValue[0])
          : undefined;
        const to = isValid(new Date(filterRule.fieldValue[1]))
          ? new Date(filterRule.fieldValue[1])
          : undefined;
        if (from) {
          dateTime = { from, to };
        }
      }
      return (
        <div>
          <DateRangePickerField
            value={dateTime}
            onChange={(value) => {
              const start = value?.from ? formatISO(value?.from) : "";
              const end = value?.to ? formatISO(value?.to) : "";
              onChange({
                ...filterRule,
                fieldValue: [start, end],
              });
            }}
            classNameContainer="min-w-50"
            className={`w-full h-8 rounded-lg px-3 text-sm placeholder:text-gray-400 border border-gray-300 focus:outline-none`}
            showClear
          />
        </div>
      );
    }
    if (filterRule.operator === "in") {
      const MAX_VISIBLE = 1;
      const SELECT_FIELD_ID = `SelectField-${new Date().getTime()}`;
      const MultiValue = (props: MultiValueProps<any>) => {
        const { index, getValue, data } = props;
        const selected = getValue();
        const overflow = selected.length - MAX_VISIBLE;
        if (index === 0) {
          if (data.color) {
            const colorCode = colorCodes.find((cc) => cc.color === data.color)?.code;
            let border = {};
            if (!colorCode) {
              border = {
                border: "1px solid #d1d5dc",
              };
            }
            return (
              <components.MultiValue
                {...props}
                innerProps={{
                  ...props.innerProps,
                  "data-tooltip-id": `${SELECT_FIELD_ID}-multiselect-tooltip`,
                  "data-tooltip-content": data?.label,
                  ...border,
                  style: {
                    backgroundColor: colorCode || "#fff",
                    color: colorCode ? "#fff" : "rgb(0, 0, 0)",
                  },
                }}
              >
                <div className={data.color ? "text-white" : ""}>{props.children}</div>
              </components.MultiValue>
            );
          }
          return (
            <components.MultiValue
              {...props}
              innerProps={{
                ...props.innerProps,
                "data-tooltip-id": `${SELECT_FIELD_ID}-multiselect-tooltip`,
                "data-tooltip-content": data?.label,
              }}
            >
              <div className={data.color ? "text-white" : ""}>{props.children}</div>
            </components.MultiValue>
          );
        }
        if (index === 1 && overflow > 0) {
          const hiddenLabels = selected
            .slice(1)
            .map((o) => o.label)
            .join(", ");
          return (
            <components.MultiValue {...props}>
              <div
                data-tooltip-id={`${SELECT_FIELD_ID}-multiselect-tooltip`}
                data-tooltip-content={hiddenLabels}
              >
                +{overflow} more
              </div>
            </components.MultiValue>
          );
        }
        return null;
      };
      const MultiValueRemove = (props: MultiValueRemoveProps<any>) => {
        const { innerProps } = props;
        delete innerProps["css"][":hover"];
        return (
          <components.MultiValueRemove
            {...props}
            innerProps={{
              ...innerProps,
            }}
          />
        );
      };
      const hasValues = filterRule.fieldValue && filterRule.fieldValue.length > 0;
      let optionsValues = [{ value: "unknown", label: "(Blanks)" }, ...valueByFields];
      if (filterRule.fieldId === "account") {
        optionsValues = [{ value: "unknown", label: "No account" }, ...accountOptions];
      } else if (filterRule.fieldId === "contact") {
        optionsValues = [{ value: "unknown", label: "No account" }, ...contactOptions];
      }
      return (
        <div>
          <SelectField
            id={SELECT_FIELD_ID}
            className="w-full text-sm"
            autoFocus={!hasValues}
            openMenuOnFocus={!hasValues}
            value={filterRule.fieldValue.map((item) =>
              optionsValues.find((opt) => opt.value === item)
            )}
            onChange={(value) => {
              onChange({
                ...filterRule,
                fieldValue: value.map((item) => item.value),
              });
            }}
            options={optionsValues}
            formatOptionLabel={(option, { context }) => {
              if (context === "value") return option.label;
              if (option.color) {
                const colorCode = colorCodes.find((cc) => cc.color === option.color)?.code;
                return (
                  <div
                    className={`w-max px-2 py-1 rounded-full ${
                      colorCode ? "" : "border border-gray-300"
                    }`}
                    style={{
                      backgroundColor: colorCode || "#fff",
                      color: colorCode ? "#fff" : "rgb(0, 0, 0)",
                    }}
                  >
                    {option.label}
                  </div>
                );
              }
              return <div className={`w-max px-2 py-1 rounded-full`}>{option.label}</div>;
            }}
            isMulti
            components={{
              MultiValue,
              MultiValueRemove,
            }}
            closeMenuOnSelect={false}
            placeholder={t("Select")}
            dropdownIndicator={<ChevronDown className="w-3 h-3 text-gray-500" />}
            stylesOverride={{
              control: (base) => ({
                ...base,
                minHeight: 30,
                height: 30,
                borderRadius: 8,
                flexWrap: "nowrap",
                borderColor: "#d1d5dc",
                boxShadow: "none",
                ":hover": {
                  borderColor: "#d1d5db",
                },
                fontSize: 12,
              }),
              menu: (base) => ({
                ...base,
                width: "max-content",
                minWidth: "100%",
              }),
            }}
          />
          <Tooltip id={`${SELECT_FIELD_ID}-multiselect-tooltip`} place="bottom" />
        </div>
      );
    }
    return (
      <div>
        <InputDebounce
          autoFocus
          type={isNumber(filterRule.fieldId) ? "number" : "text"}
          className={`w-full h-8 rounded-lg px-3 text-sm placeholder:text-gray-400 border border-gray-300 focus:outline-none`}
          placeholder={t("Type")}
          value={filterRule.fieldValue.length > 0 ? filterRule.fieldValue[0] : ""}
          onChange={(value: any) => {
            onChange({
              ...filterRule,
              fieldValue: value ? [isNumber(filterRule.fieldId) ? parseFloat(value) : value] : [],
            });
          }}
        />
      </div>
    );
  };

  return (
    <div className="w-full flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        <div>
          {idx == 0 ? (
            <div className="w-max px-2 py-1 text-sm rounded-lg border border-dotted border-gray-300 text-gray-500">
              {t("Where")}
            </div>
          ) : (
            <div className="w-max px-2 py-1 text-sm rounded-lg border border-dotted border-gray-300 text-gray-500">
              {t("And")}
            </div>
          )}
        </div>
        <div>
          <SelectField
            className="w-max"
            autoFocus={!filterRule.fieldId}
            openMenuOnFocus={!filterRule.fieldId}
            value={availableFields.find((item) => item.value === filterRule.fieldId)}
            onChange={(value) => {
              onChange({
                operator: "",
                fieldType: value?.data?.type || "",
                fieldId: value?.value || "",
                fieldValue: [],
              });
            }}
            options={availableFields}
            placeholder={t("Field")}
            dropdownIndicator={<ChevronDown className="w-3 h-3 text-gray-500" />}
            formatOptionLabel={(option) => {
              return (
                <div className={"w-full flex items-center space-x-2"} key={option.value}>
                  <div>{renderFieldIcon(option.data || {})}</div>
                  <div>{t(option.label)}</div>
                </div>
              );
            }}
            stylesOverride={{
              control: (base) => ({
                ...base,
                minHeight: 30,
                height: 30,
                borderRadius: 8,
                borderColor: "#d1d5dc",
                boxShadow: "none",
                ":hover": {
                  borderColor: "#d1d5db",
                },
                fontSize: 12,
              }),
              menu: (base) => ({
                ...base,
                width: "max-content",
                minWidth: "100%",
              }),
            }}
          />
        </div>
        {filterRule.fieldId ? (
          <>
            <div>
              <SelectField
                className="w-max"
                autoFocus={!filterRule.operator}
                openMenuOnFocus={!filterRule.operator}
                value={getConditionOptions().find((item) => item.value === filterRule.operator)}
                onChange={(value) => {
                  const op = value?.value || "";
                  if (noValueOperators.has(op)) {
                    onChange({
                      ...filterRule,
                      operator: op,
                      fieldValue: ["__" + op + "__"],
                    });
                  } else {
                    onChange({
                      ...filterRule,
                      operator: op,
                    });
                  }
                }}
                options={getConditionOptions()}
                placeholder={t("Condition")}
                dropdownIndicator={<ChevronDown className="w-3 h-3 text-gray-500" />}
                stylesOverride={{
                  control: (base) => ({
                    ...base,
                    minHeight: 30,
                    height: 30,
                    borderRadius: 8,
                    borderColor: "#d1d5dc",
                    boxShadow: "none",
                    ":hover": {
                      borderColor: "#d1d5db",
                    },
                    fontSize: 12,
                  }),
                  menu: (base) => ({
                    ...base,
                    width: "max-content",
                    minWidth: "100%",
                  }),
                }}
              />
            </div>
            {filterRule.operator && !noValueOperators.has(filterRule.operator)
              ? renderFieldValue()
              : null}
          </>
        ) : null}
      </div>
      <div className="p-1 flex items-center rounded-md hover:bg-gray-100">
        <X
          className={"w-5 h-5 text-gray-400 cursor-pointer"}
          onClick={() => {
            onRemove(idx);
          }}
        />
      </div>
    </div>
  );
};

export default AdvancedFilterItem;
